import {Card, DataStatus, TabProps, Tabs} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {useState} from 'react';

import {defaultTo, isNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCurrenciesQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetPaymentTypeEnumQuery} from '@dms/api/finance';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  GetReceiveNoteCorrectionResponse,
  GetReceiveNoteResponse,
  useGetSuppliersQuery,
  useGetWarehouseQuery,
  useGetWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  DEFAULT_CURRENCY,
  queryParams,
  refreshDmsDatagrid,
  Section,
  usePermissions,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, suffixTestId, useBoolean, useQueryState} from 'shared/web';

import {BasicInformationForm} from './components/BasicInformationForm';
import {GeneralInformationTab} from './components/GeneralInformationTab';
import {ListOfItemsTab} from './components/ListOfItemsTab';
import {ReceiveNoteCorrectionAddMaterialModal} from './components/ReceiveNoteCorrectionAddMaterialModal';

interface OverviewProps extends RequiredTestIdProps {
  isReceiveNoteCorrectionLoading: boolean;
  receiveNote?: GetReceiveNoteResponse;
  receiveNoteCorrection?: GetReceiveNoteCorrectionResponse;
}

export function Overview(props: OverviewProps) {
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const {receiveNoteCorrectionId} = useWarehouseParams();

  const [isMaterialModalVisible, openMaterialModal, closeMaterialModal] = useBoolean();

  const receiveNoteId = defaultTo('', props.receiveNoteCorrection?.receiveNote?.receiveNoteId);

  const [isCreatingReceiveNoteCorrection, setIsCreatingReceiveNoteCorrection] = useState<boolean>(
    isNilOrEmpty(receiveNoteCorrectionId)
  );

  const [receiveNoteCorrectionEdit] = usePermissions({
    permissionKeys: ['receiveNoteCorrectionEdit'],
  });

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSuppliersQuery();

  const {
    data: warehouses,
    isLoading: areWarehousesLoading,
    isError: hasWarehousesError,
  } = useGetWarehousesQuery();

  const {
    data: warehouseByReceiveNoteCorrection,
    isLoading: isWarehouseByReceiveNoteCorrectionLoading,
    isError: hasWarehouseByReceiveNoteCorrectionError,
  } = useGetWarehouseQuery(
    {warehouseId: props.receiveNoteCorrection?.creditNote?.warehouseId!},
    {skip: isNil(props.receiveNoteCorrection?.creditNote?.warehouseId)}
  );

  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const {
    data: authorizationProfiles,
    isLoading: areAuthorizationProfilesLoading,
    isError: hasAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id!}, {skip: isNil(tenant)});

  const {
    data: paymentTypes,
    isLoading: arePaymentTypesLoading,
    isError: hasPaymentTypesError,
  } = useGetPaymentTypeEnumQuery();

  const {
    data: currencies,
    isLoading: areCurrenciesLoading,
    isError: hasCurrenciesError,
  } = useGetCurrenciesQuery();

  const isLoading =
    areSuppliersLoading ||
    areWarehousesLoading ||
    isWarehouseByReceiveNoteCorrectionLoading ||
    isTenantLoading ||
    areAuthorizationProfilesLoading ||
    arePaymentTypesLoading ||
    areCurrenciesLoading;

  const hasError =
    hasSuppliersError ||
    hasWarehousesError ||
    hasWarehouseByReceiveNoteCorrectionError ||
    hasTenantError ||
    hasAuthorizationProfilesError ||
    hasPaymentTypesError ||
    hasCurrenciesError;

  const isReceiveNoteCorrectionPending =
    props.receiveNoteCorrection?.creditNote?.state === 'PENDING';

  const currencyByReceiveNoteCorrectionWarehouse = defaultTo(
    tenant?.currency ?? DEFAULT_CURRENCY,
    warehouseByReceiveNoteCorrection?.currency
  );

  const handleClose = () => {
    closeMaterialModal();
    refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseCreditNoteItem, {deselectAll: true});
  };

  const tabs: TabProps[] = [
    {
      title: i18n.t('entity.warehouse.labels.generalInformation'),
      id: 'general-information',
      content: (
        <GeneralInformationTab
          receiveNoteCorrection={props.receiveNoteCorrection}
          suppliers={suppliers}
          warehouses={warehouses}
          authorizationProfiles={authorizationProfiles}
          paymentTypes={paymentTypes}
          currencies={currencies}
          data-testid={suffixTestId('generalInformation', props)}
        />
      ),
    },
    {
      title: i18n.t('entity.warehouse.labels.listOfItems'),
      id: 'list-of-items',
      actions: [
        {
          type: 'button',
          variant: 'outlined',
          title: i18n.t('entity.warehouse.labels.addMaterial'),
          isDisabled: not(isReceiveNoteCorrectionPending) || !receiveNoteCorrectionEdit,
          tooltip: !receiveNoteCorrectionEdit
            ? i18n.t('module.warehouse.label.missingReceiveNoteCorrectionEditPermission')
            : undefined,
          onClick: () => openMaterialModal(),
        },
      ],
      content: (
        <ListOfItemsTab
          receiveNoteId={receiveNoteId}
          isCreatingReceiveNoteCorrection={isCreatingReceiveNoteCorrection}
          isReceiveNoteCorrectionPending={isReceiveNoteCorrectionPending}
          totalPrice={props.receiveNoteCorrection?.total}
          totalPriceCurrency={currencyByReceiveNoteCorrectionWarehouse}
          data-testid={suffixTestId('listOfItems', props)}
        />
      ),
    },
  ];

  return (
    <Section isFullHeight data-testid={props['data-testid']}>
      <DataStatus
        isLoading={isLoading}
        isError={hasError}
        minHeight="100vh"
        data-testid={suffixTestId('status', props)}
      >
        <Box height="100%">
          {isCreatingReceiveNoteCorrection ? (
            <Card
              title={i18n.t('general.labels.basicInformation')}
              data-testid={suffixTestId('cards.basicInformation', props)}
            >
              <BasicInformationForm
                isCreatingReceiveNoteCorrection={isCreatingReceiveNoteCorrection}
                receiveNote={props.receiveNote}
                receiveNoteCorrection={props.receiveNoteCorrection}
                suppliers={suppliers}
                warehouses={warehouses}
                authorizationProfiles={authorizationProfiles}
                paymentTypes={paymentTypes}
                currencies={currencies}
                onClose={() => setIsCreatingReceiveNoteCorrection(false)}
                data-testid={suffixTestId('forms.basicInformation', props)}
              />
            </Card>
          ) : (
            <>
              <Tabs
                isFullHeight
                tabs={tabs}
                activeTabId={activeTabId}
                onChange={setActiveTabId}
                isLazy
                variant="condensed"
              />

              <Show when={isMaterialModalVisible}>
                <ReceiveNoteCorrectionAddMaterialModal
                  receiveNoteId={receiveNoteId}
                  receiveNoteCorrectionId={receiveNoteCorrectionId}
                  isReceiveNoteCorrectionCompleted={!isReceiveNoteCorrectionPending}
                  onClose={handleClose}
                  data-testid={suffixTestId('addMaterial', props)}
                />
              </Show>
            </>
          )}
        </Box>
      </DataStatus>
    </Section>
  );
}
